// WB-M1B S4.1 (doc 23 §25) -- the visual authoring field of the board's
// Equation editor: what the Student composes an equation IN. Formatted
// mathematics as it is typed, structures with editable slots, LaTeX
// underneath.
//
// An authoring ADAPTER and nothing more: visual math <-> a LaTeX string. It
// decides nothing about what the board accepts (the S1 validator / renderer
// in `boardEquation.js` does, on the LaTeX this field produces), draws
// nothing on the board, and persists nothing.
//
// The field itself is MathLive's <math-field>, which lives in
// `boardEquationFieldEngine.js` and is loaded on first need as its own
// chunk. This module is the light part: the loader, the palette, and the
// typing shortcuts the field is allowed to have.

// Doubled in the specs the way the store modules' collaborators are.
export const collaborators = {
  importField: () =>
    import(
      /* webpackChunkName: "ai-tutor-board-mathfield" */ './boardEquationFieldEngine'
    ),
}

let fieldPromise = null
let fieldEngine = null

export function isEquationFieldLoaded() {
  return fieldEngine !== null
}

/**
 * Loads the visual field (once). A failed load is not remembered, so a later
 * call tries again.
 * @returns {Promise<{createField: Function}>} the engine
 * @throws {Error} `code: 'field_unavailable'`
 */
export function loadEquationField() {
  if (!fieldPromise) {
    fieldPromise = Promise.resolve()
      .then(() => collaborators.importField())
      .then(loaded => {
        if (!loaded || typeof loaded.createField !== 'function') {
          throw new Error('no field')
        }
        fieldEngine = loaded
        return loaded
      })
      .catch(() => {
        fieldPromise = null
        fieldEngine = null
        const error = new Error('field_unavailable')
        error.code = 'field_unavailable'
        throw error
      })
  }
  return fieldPromise
}

// Spec-only: forget the loaded field engine.
export function resetEquationFieldForTests() {
  fieldPromise = null
  fieldEngine = null
}

// A structure whose slot the Student has not filled yet: the field writes an
// empty slot as this command. Such an equation is unfinished, not wrong.
const EMPTY_SLOT = /\\placeholder(?![A-Za-z])/

/** @param {string} latex -- what the field produced */
export function hasEmptySlot(latex) {
  return EMPTY_SLOT.test(latex)
}

// ---------------------------------------------------------------------------
// The palette: a compact, secondary-school set. Each key inserts `latex` at
// the caret. In a template, `#@` is the selection or, with none, what stands
// just before the caret (so "1" then fraction makes 1 the numerator); `#0` is
// the selection or an empty slot; `#?` is an empty slot. `slot: true` leaves
// the caret in the first empty slot; otherwise it goes after what was
// inserted.
//
// `glyph` is what the key shows (with `sup` / `sub` / `pre` raised or lowered
// beside it, `over` under a fraction bar); its NAME, for assistive
// technology and as its tooltip, is the locale string
// `aiTutorBoard.equation.keys.<id>`.
// ---------------------------------------------------------------------------
export const BOARD_EQUATION_PALETTE = Object.freeze([
  {
    id: 'basic',
    keys: [
      { id: 'plus', glyph: '+', latex: '+' },
      { id: 'minus', glyph: '−', latex: '-' },
      { id: 'times', glyph: '×', latex: '\\times' },
      { id: 'divide', glyph: '÷', latex: '\\div' },
      { id: 'equals', glyph: '=', latex: '=' },
      { id: 'notEqual', glyph: '≠', latex: '\\ne' },
      { id: 'less', glyph: '<', latex: '<' },
      { id: 'greater', glyph: '>', latex: '>' },
      { id: 'lessEqual', glyph: '≤', latex: '\\le' },
      { id: 'greaterEqual', glyph: '≥', latex: '\\ge' },
    ],
  },
  {
    id: 'structure',
    keys: [
      {
        id: 'fraction',
        glyph: 'x',
        over: 'y',
        latex: '\\frac{#@}{#?}',
        slot: true,
      },
      { id: 'power', glyph: 'x', sup: 'n', latex: '#@^{#?}', slot: true },
      { id: 'square', glyph: 'x', sup: '2', latex: '#@^2' },
      { id: 'subscript', glyph: 'x', sub: 'n', latex: '#@_{#?}', slot: true },
      { id: 'squareRoot', glyph: '√x', latex: '\\sqrt{#0}', slot: true },
      {
        id: 'nthRoot',
        glyph: '√x',
        pre: 'n',
        latex: '\\sqrt[#?]{#0}',
        slot: true,
      },
      {
        id: 'parentheses',
        glyph: '(x)',
        latex: '\\left(#0\\right)',
        slot: true,
      },
      {
        id: 'absoluteValue',
        glyph: '|x|',
        latex: '\\left|#0\\right|',
        slot: true,
      },
    ],
  },
  {
    id: 'common',
    keys: [
      { id: 'pi', glyph: 'π', latex: '\\pi' },
      { id: 'theta', glyph: 'θ', latex: '\\theta' },
      { id: 'sin', glyph: 'sin', latex: '\\sin', word: true },
      { id: 'cos', glyph: 'cos', latex: '\\cos', word: true },
      { id: 'tan', glyph: 'tan', latex: '\\tan', word: true },
      { id: 'log', glyph: 'log', latex: '\\log', word: true },
      { id: 'ln', glyph: 'ln', latex: '\\ln', word: true },
    ],
  },
  {
    id: 'science',
    keys: [
      { id: 'alpha', glyph: 'α', latex: '\\alpha' },
      { id: 'beta', glyph: 'β', latex: '\\beta' },
      { id: 'gamma', glyph: 'γ', latex: '\\gamma' },
      { id: 'delta', glyph: 'Δ', latex: '\\Delta' },
      { id: 'arrow', glyph: '→', latex: '\\rightarrow' },
      { id: 'equilibrium', glyph: '⇌', latex: '\\rightleftharpoons' },
    ],
  },
])

// ---------------------------------------------------------------------------
// Typing shortcuts: what a run of plain keys turns into while the Student
// types ("pi" becomes π, "<=" becomes ≤). MathLive ships about 240 of them;
// many write commands the board's renderer does not have, or would surprise
// a secondary-school Student ("th", "or", "xx", "cm" ...). The field gets
// THIS table instead: every entry is LaTeX the S1 renderer draws (pinned by
// boardEquationField.spec.js).
// ---------------------------------------------------------------------------
export const BOARD_EQUATION_SHORTCUTS = Object.freeze({
  // characters that must not reach LaTeX bare
  '%': '\\%',
  '&': '\\&',
  $: '\\$',
  // operators and relations
  '*': '\\cdot',
  '+-': '\\pm',
  '<=': '\\le',
  '>=': '\\ge',
  '!=': '\\ne',
  '≤': '\\le',
  '≥': '\\ge',
  '≠': '\\ne',
  '÷': '\\div',
  '~~': '\\approx',
  '≈': '\\approx',
  '->': '\\to',
  '=>': '\\Rightarrow',
  '<=>': '\\Leftrightarrow',
  '...': '\\ldots',
  '∞': '\\infty',
  infty: '\\infty',
  // Greek letters by name
  alpha: '\\alpha',
  beta: '\\beta',
  gamma: '\\gamma',
  delta: '\\delta',
  Delta: '\\Delta',
  epsilon: '\\epsilon',
  theta: '\\theta',
  lambda: '\\lambda',
  mu: '\\mu',
  pi: '\\pi',
  rho: '\\rho',
  sigma: '\\sigma',
  Sigma: '\\Sigma',
  tau: '\\tau',
  phi: '\\phi',
  omega: '\\omega',
  Omega: '\\Omega',
  // functions
  sin: '\\sin',
  cos: '\\cos',
  tan: '\\tan',
  cot: '\\cot',
  sec: '\\sec',
  csc: '\\csc',
  arcsin: '\\arcsin',
  arccos: '\\arccos',
  arctan: '\\arctan',
  log: '\\log',
  ln: '\\ln',
  exp: '\\exp',
  max: '\\max',
  min: '\\min',
  lim: '\\lim_{#?}',
  // structures by name
  sqrt: '\\sqrt{#?}',
  sum: '\\sum_{#?}^{#?}',
  int: '\\int_{#?}^{#?}',
})
